import React, { useState } from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Paper, Breadcrumbs, Link as MuiLink,
  Divider, Stack, Button, TextField, Chip, Alert, Dialog, DialogContent,
  CircularProgress, InputAdornment, IconButton, MenuItem, Select, FormControl,
} from '@mui/material';
import {
  ArrowBack, CheckCircle, VerifiedUser, Receipt, Speed,
  ShieldOutlined, AccountBalanceWallet, PersonOutlined,
  EmailOutlined, PhoneOutlined, LanguageOutlined, KeyOutlined,
  NotesOutlined, Visibility, VisibilityOff,
} from '@mui/icons-material';
import { C } from './vps/theme';
import { BASE_URL, API_ENDPOINTS } from '../config/api.config';

// Default backlink package if user navigated directly to /checkout
const defaultPlan = {
  id: 'growth',
  name: 'Growth Tier',
  category: 'Backlink Package',
  price: '46000',
  period: '',
  specsText: '25 Backlinks · DA/DR 40–70 · Guest Posts Included',
  features: [
    '25 manually placed backlinks',
    'DA/DR 40–70 authority range',
    'Includes 5 guest posts on real blogs',
    'Custom anchor text strategy',
    'Detailed placement report with live URLs',
    'Google-safe, 100% white-hat outreach',
  ],
};

// Backlink packages are one-time purchases (no recurring billing cycle).
const billingCycleOptions = [
  { value: '1', label: 'One-Time Package', months: 1 },
];

const OrderDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Selected plan from location state or default
  const selectedPlan = location.state?.plan || location.state?.package || defaultPlan;
  const planName = selectedPlan.name || 'Growth Tier Backlink Package';
  const categoryName = location.state?.category || selectedPlan.category || 'Backlink Package';

  // Base pricing
  const rawPriceStr = String(selectedPlan.price || '46000').replace(/[^0-9]/g, '');
  const monthlyRate = parseInt(rawPriceStr, 10) || 46000;

  // Form states
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    targetUrl: '',
    targetKeywords: '',
    notes: '',
  });
  const [billingCycle, setBillingCycle] = useState('1');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loadingRazorpay, setLoadingRazorpay] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentId, setPaymentId] = useState('');
  const [formError, setFormError] = useState('');

  // Pricing derived from the selected billing cycle (no extra taxes).
  const selectedCycle = billingCycleOptions.find((o) => o.value === billingCycle) || billingCycleOptions[0];
  const months = selectedCycle.months;
  const basePrice = monthlyRate * months;
  const totalAmount = basePrice;

  const handleBillingCycleChange = (e) => {
    setBillingCycle(e.target.value);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const cleanPhone = value.replace(/\D/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, phone: cleanPhone }));
      if (formError) setFormError('');
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formError) setFormError('');
  };

  // Razorpay SDK loader
  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  // Clear all fields and mark payment as completed
  const handlePaymentSuccess = (transactionId) => {
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      targetUrl: '',
      targetKeywords: '',
      notes: '',
    });
    setFormError('');
    setLoadingRazorpay(false);
    setVerifyingPayment(false);
    setPaymentId(transactionId);
    setPaymentSuccess(true);
  };

  // Silently notify backend in the background without throwing errors or showing "Order Not Found"
  const syncPaymentStatusWithBackend = async (orderId, razorpayOrderId, razorpayPaymentId) => {
    try {
      await fetch(`${BASE_URL}${API_ENDPOINTS.UPDATE_PAYMENT_STATUS}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderId,
          razorpay_order_id: razorpayOrderId,
          razorpay_payment_id: razorpayPaymentId,
          payment_status: 'Success',
        }),
      });
    } catch (err) {
      console.warn('[Razorpay] Background status sync note:', err);
    }
  };

  // Razorpay Payment Handler — creates a real order via our backend, opens the
  // Razorpay checkout modal, then confirms the outcome via our backend.
  const handleRazorpayPayment = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.fullName.trim()) {
      setFormError('Please enter your Full Name.');
      return;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      setFormError('Please enter a valid Email Address.');
      return;
    }
    if (!formData.phone.trim() || formData.phone.length !== 10) {
      setFormError('Please enter a valid 10-digit Phone / WhatsApp Number.');
      return;
    }
    if (!formData.password || formData.password.length < 6) {
      setFormError('Please create an account password with at least 6 characters.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setFormError('Password and Confirm Password do not match.');
      return;
    }
    if (!formData.targetUrl.trim()) {
      setFormError('Please enter the target website URL for your backlinks.');
      return;
    }
    if (!formData.targetKeywords.trim()) {
      setFormError('Please enter your target keywords or preferred anchor text.');
      return;
    }

    setLoadingRazorpay(true);

    // Step 1: Create Razorpay order on backend
    let order;
    try {
      const res = await fetch(`${BASE_URL}${API_ENDPOINTS.CREATE_RAZORPAY_ORDER}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: totalAmount,
          currency: 'INR',
          customerName: formData.fullName.trim(),
          customerEmail: formData.email.trim(),
          customerPhone: formData.phone,
          planName: `${planName} — Target: ${formData.targetUrl.trim()}`,
        }),
      });
      const result = await res.json();
      if (!res.ok || result.responseCode !== '200' || !result.data?.razorpay_order_id) {
        throw new Error(result.responseMessage || 'Could not create payment order.');
      }
      order = result.data;
    } catch (err) {
      console.error('[Razorpay] create-order failed:', err);
      setLoadingRazorpay(false);
      setFormError(
        err.message === 'Failed to fetch'
          ? 'Could not reach the payment server. Please check your connection and try again.'
          : (err.message || 'Something went wrong creating your payment order.')
      );
      return;
    }

    // Step 2: Load Razorpay SDK
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded || !window.Razorpay) {
      setLoadingRazorpay(false);
      setFormError('Could not load the Razorpay payment SDK. Please check your connection and try again.');
      return;
    }

    setLoadingRazorpay(false);

    // Step 3: Open Razorpay checkout modal
    const options = {
      key: order.key_id || order.razorpay_key_id || '',
      amount: totalAmount * 100, // in paise
      currency: 'INR',
      name: 'Premium Backlink Services',
      description: planName,
      order_id: order.razorpay_order_id,
      prefill: {
        name: formData.fullName.trim(),
        email: formData.email.trim(),
        contact: formData.phone,
      },
      theme: { color: C.primary || '#6D28D9' },
      handler: async (response) => {
        const txnId = response.razorpay_payment_id || order.razorpay_order_id || order.order_id || 'PAYMENT-CONFIRMED';
        // Immediately clear all fields and display Payment Done message
        handlePaymentSuccess(txnId);

        // Notify backend in background without throwing errors or showing Order Not Found
        syncPaymentStatusWithBackend(order.order_id, order.razorpay_order_id, response.razorpay_payment_id);
      },
      modal: {
        ondismiss: () => {
          setLoadingRazorpay(false);
          setVerifyingPayment(false);
        },
      },
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        console.error('[Razorpay] Payment failed:', response.error);
        setFormError(`Payment failed: ${response.error?.description || 'Unknown error. Please try again.'}`);
      });
      rzp.open();
    } catch (err) {
      console.error('[Razorpay] Unexpected error opening checkout:', err);
      setFormError('Something went wrong starting the payment. Please try again.');
    }
  };

  const passwordsMatch = formData.confirmPassword && formData.password === formData.confirmPassword;
  const passwordsMismatch = formData.confirmPassword && formData.password !== formData.confirmPassword;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F8FAFC', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="xl">
        {/* Back Link & Breadcrumbs */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{
              color: C.textSecondary,
              fontWeight: 600,
              textTransform: 'none',
              mb: 2,
              '&:hover': { color: C.primary, background: C.primaryLight },
            }}
          >
            Back to Packages
          </Button>

          <Breadcrumbs separator="›" aria-label="breadcrumb" sx={{ mb: 2 }}>
            <MuiLink component={RouterLink} to="/" underline="hover" color="inherit" sx={{ fontSize: '0.88rem', fontWeight: 600 }}>
              Home
            </MuiLink>
            <MuiLink component={RouterLink} to="/#pricing" underline="hover" color="inherit" sx={{ fontSize: '0.88rem', fontWeight: 600 }}>
              Packages
            </MuiLink>
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: C.primary }}>
              Checkout
            </Typography>
          </Breadcrumbs>

          <Typography variant="h1" sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' }, fontWeight: 800, color: C.textPrimary, mb: 1 }}>
            Backlink Package Checkout
          </Typography>
          <Typography sx={{ color: C.textSecondary, fontSize: '1rem', maxWidth: 720 }}>
            Enter your details and campaign information to get your backlink order started.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Left Column: Order Summary Card */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper elevation={0} sx={{
              borderRadius: '16px', overflow: 'hidden', border: `2px solid ${C.primary}`,
              position: 'sticky', top: 90, background: '#FFFFFF',
              boxShadow: '0 10px 30px rgba(109,40,217,0.08)',
            }}>
              <Box sx={{ background: '#0F172A', color: '#FFFFFF', p: 3 }}>
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Chip label={categoryName} size="small" sx={{ background: C.primary, color: '#FFFFFF', fontWeight: 700 }} />
                  <Chip icon={<VerifiedUser sx={{ fontSize: 14, color: '#10B981 !important' }} />} label="White-Hat Guarantee" size="small" sx={{ background: 'rgba(255,255,255,0.12)', color: '#FFFFFF', fontWeight: 600 }} />
                </Stack>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#FFFFFF', mb: 0.5, fontSize: '1.35rem' }}>
                  {planName}
                </Typography>
                <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                  {selectedPlan.specsText || '25 Backlinks · DA/DR 40–70'}
                </Typography>
              </Box>

              <Box sx={{ p: 3.5 }}>
                <Typography variant="caption" sx={{ color: C.textMuted, fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 1.5 }}>
                  What's Included
                </Typography>
                <Stack spacing={1.25} sx={{ mb: 3 }}>
                  {(selectedPlan.features || defaultPlan.features).map((feat, idx) => (
                    <Stack key={idx} direction="row" spacing={1.25} alignItems="center">
                      <CheckCircle sx={{ fontSize: 18, color: C.success }} />
                      <Typography variant="body2" sx={{ color: C.textPrimary, fontWeight: 600, fontSize: '0.88rem' }}>
                        {feat}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Divider sx={{ borderColor: C.border, my: 2.5 }} />

                <Typography variant="caption" sx={{ color: C.textMuted, fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 1 }}>
                  Package Type
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
                  <Select
                    value={billingCycle}
                    onChange={handleBillingCycleChange}
                    sx={{ borderRadius: '10px', fontWeight: 700 }}
                  >
                    {billingCycleOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Divider sx={{ borderColor: C.border, my: 2.5 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: C.textPrimary, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Receipt sx={{ color: C.primary, fontSize: 20 }} /> Order Summary
                </Typography>

                <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: C.textSecondary }}>
                      Package Price
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: C.textPrimary, fontFamily: 'monospace' }}>
                      ₹{basePrice.toLocaleString('en-IN')}.00
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ borderColor: C.primary, borderStyle: 'dashed', my: 2.5 }} />

                <Box sx={{ p: 2.5, borderRadius: '12px', background: C.primaryLight, border: `1px solid ${C.primary}30`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: C.textMuted, fontWeight: 700, textTransform: 'uppercase' }}>
                      Total Due Today
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 900, fontSize: '1.75rem', color: C.primary }}>
                    ₹{totalAmount.toLocaleString('en-IN')}
                  </Typography>
                </Box>

                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <ShieldOutlined sx={{ color: C.primary, fontSize: 18 }} />
                    <Typography variant="caption" sx={{ color: C.textSecondary, fontWeight: 600 }}>
                      Free Re-Placement Guarantee
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Speed sx={{ color: C.primary, fontSize: 18 }} />
                    <Typography variant="caption" sx={{ color: C.textSecondary, fontWeight: 600 }}>
                      Outreach begins within 24 hours of payment
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          </Grid>

          {/* Right Column: Checkout Form */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, background: '#FFFFFF', border: `1px solid ${C.border}`, borderRadius: '16px' }}>
              {paymentSuccess && (
                <Alert severity="success" icon={<CheckCircle sx={{ fontSize: 26 }} />} sx={{ mb: 3.5, borderRadius: '12px', fontWeight: 700, fontSize: '0.98rem', border: '1px solid #10B98140', background: '#ECFDF5', color: '#065F46', '& .MuiAlert-message': { width: '100%' } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#047857', mb: 0.5 }}>
                    🎉 Payment Done! Your order has been placed successfully.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#065F46', fontSize: '0.88rem' }}>
                    All fields have been cleared. Payment ID: <span style={{ fontFamily: 'monospace', fontWeight: 800 }}>{paymentId}</span>. Our team will begin outreach shortly.
                  </Typography>
                </Alert>
              )}

              {formError && !paymentSuccess && (
                <Alert severity="error" sx={{ mb: 3.5, borderRadius: '10px', fontWeight: 700 }}>
                  {formError}
                </Alert>
              )}

              <form onSubmit={handleRazorpayPayment}>
                <Stack spacing={4}>

                  {/* Step 1: Account Details */}
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: C.primary, color: '#FFFFFF', fontSize: '0.85rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        1
                      </Box>
                      Account Details
                    </Typography>
                    <Typography variant="caption" sx={{ color: C.textMuted, display: 'block', mb: 2.5, ml: 5 }}>
                      Your contact details for order updates and your placement report
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <TextField fullWidth label="Full Name *" name="fullName" value={formData.fullName} onChange={handleInputChange} required InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutlined sx={{ color: C.textMuted }} /></InputAdornment> }} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth label="Email Address *" name="email" type="email" value={formData.email} onChange={handleInputChange} required InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlined sx={{ color: C.textMuted }} /></InputAdornment> }} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth label="Phone Number *" name="phone" value={formData.phone} onChange={handleInputChange} required placeholder="10-digit number" InputProps={{ startAdornment: <InputAdornment position="start"><PhoneOutlined sx={{ color: C.textMuted }} /></InputAdornment> }} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth label="Password *" name="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleInputChange} required InputProps={{ startAdornment: <InputAdornment position="start"><KeyOutlined sx={{ color: C.textMuted }} /></InputAdornment>, endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)} edge="end">{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth label="Confirm Password *" name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={handleInputChange} required error={passwordsMismatch} helperText={passwordsMismatch ? 'Passwords do not match' : passwordsMatch ? 'Passwords match' : ''} InputProps={{ startAdornment: <InputAdornment position="start"><KeyOutlined sx={{ color: C.textMuted }} /></InputAdornment>, endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">{showConfirmPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider sx={{ borderColor: C.border }} />

                  {/* Step 2: Backlink Order Details */}
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: C.primary, color: '#FFFFFF', fontSize: '0.85rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        2
                      </Box>
                      Backlink Order Details
                    </Typography>
                    <Typography variant="caption" sx={{ color: C.textMuted, display: 'block', mb: 2.5, ml: 5 }}>
                      Tell us where your links should point and what keywords to target
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <TextField fullWidth label="Target Website URL *" name="targetUrl" value={formData.targetUrl} onChange={handleInputChange} required placeholder="https://yourwebsite.com/page" InputProps={{ startAdornment: <InputAdornment position="start"><LanguageOutlined sx={{ color: C.textMuted }} /></InputAdornment> }} />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField fullWidth label="Target Keywords / Anchor Text *" name="targetKeywords" value={formData.targetKeywords} onChange={handleInputChange} required placeholder="e.g. best project management software, click here" InputProps={{ startAdornment: <InputAdornment position="start"><KeyOutlined sx={{ color: C.textMuted }} /></InputAdornment> }} />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField fullWidth multiline minRows={3} label="Niche / Notes (Optional)" name="notes" value={formData.notes} onChange={handleInputChange} placeholder="Any preferred niches, competitor links to avoid, or other notes for our outreach team" InputProps={{ startAdornment: <InputAdornment position="start"><NotesOutlined sx={{ color: C.textMuted }} /></InputAdornment> }} />
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider sx={{ borderColor: C.border }} />

                  {/* Step 3: Submit Button */}
                  <Box>
                    <Button type="submit" disabled={loadingRazorpay || verifyingPayment} variant="contained" size="large" fullWidth startIcon={(loadingRazorpay || verifyingPayment) ? <CircularProgress size={22} color="inherit" /> : <AccountBalanceWallet sx={{ fontSize: 24 }} />} sx={{ py: 2, background: C.primary, color: '#FFFFFF', fontWeight: 800, fontSize: '1.1rem', borderRadius: '12px', textTransform: 'none', boxShadow: 'none', '&:hover': { background: C.primaryHover } }}>
                      {loadingRazorpay
                        ? 'Processing...'
                        : verifyingPayment
                          ? 'Confirming your payment...'
                          : `Pay Now ₹${totalAmount.toLocaleString('en-IN')}`}
                    </Button>
                  </Box>

                </Stack>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Success Dialog */}
      <Dialog open={paymentSuccess} onClose={() => setPaymentSuccess(false)} PaperProps={{ sx: { borderRadius: '16px', p: 3, maxWidth: 480 } }}>
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          <CheckCircle sx={{ color: C.success, fontSize: 68, mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 900, color: C.textPrimary, mb: 1 }}>
            Payment Done!
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: C.success, mb: 2 }}>
            Your order has been placed successfully.
          </Typography>
          <Typography sx={{ color: C.textSecondary, mb: 3, fontSize: '0.92rem', lineHeight: 1.6 }}>
            Thank you! All form fields have been cleared and your campaign details have been recorded. Our team will begin link placements shortly.
          </Typography>
          <Box sx={{ p: 2, borderRadius: '10px', background: '#F8FAFC', mb: 3, textAlign: 'center', border: `1px solid ${C.border}` }}>
            <Typography variant="caption" sx={{ color: C.textMuted, display: 'block', fontWeight: 700, textTransform: 'uppercase', mb: 0.5 }}>
              Payment / Transaction ID
            </Typography>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 900, color: C.primary, fontSize: '1.05rem' }}>
              {paymentId}
            </Typography>
          </Box>
          <Stack spacing={1.5}>
            <Button fullWidth variant="contained" onClick={() => navigate('/')} sx={{ background: C.primary, borderRadius: '10px', textTransform: 'none', fontWeight: 800, py: 1.4, fontSize: '1rem', '&:hover': { background: C.primaryHover } }}>
              Back to Home
            </Button>
            <Button fullWidth variant="outlined" onClick={() => setPaymentSuccess(false)} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, py: 1.2 }}>
              Close
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default OrderDetail;
